import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  Header,
  Image,
  Segment,
  Message,
  Loader,
  Pagination,
  Divider,
  Icon,
  Button,
  Dropdown
} from 'semantic-ui-react';
// Clean architecture: Import from data slice for opportunity operations
import {
  fetchOpportunities,
  selectOpportunities,
  selectOpportunitiesStatus,
  selectOpportunitiesError,
  selectOpportunityPaging,
  setFilters,
  setSort,
  readStoredSort
} from '../../../slices/opportunityDataSlice';
import {
  fetchActorApplications,
  selectActorApplications
} from '../../../slices/applicationSlice';
import OpportunityCard from '../../../components/opportunities/OpportunityCard';
import OpportunitySpreadsheet from '../../../components/opportunities/OpportunitySpreadsheet';
import OpportunityFilterEnhanced from '../../../components/opportunities/OpportunityFilterEnhanced';
import { HeaderAndPage } from '../../../common/components';
import { selectActiveRole } from '../../../slices/userRoleSlice';
import microphoneStand from '../../../assets/microphone-stand.svg'
import { AuthStdFooter } from "../../../utils/auth-std-footer"

/**
 * OpportunityMarketplace component displays a marketplace of voice acting opportunities
 */
const OpportunityMarketplace = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get opportunities from Redux store
  const opportunities = useSelector(selectOpportunities);
  const status = useSelector(selectOpportunitiesStatus);
  const error = useSelector(selectOpportunitiesError);

  // Get the current user and actor ID
  const currentUser = useSelector(state => state.user.profile.user);
  const actorId = currentUser?.actorId;

  // Check if the user's active role is voiceActor (required to apply)
  const activeRole = useSelector(selectActiveRole);
  const isVoiceActor = activeRole === 'voiceActor';

  // Get applications for the current actor
  const actorApplications = useSelector(selectActorApplications(actorId || ''));

  // Server-side pagination.
  //
  // `opportunities` now holds ONE PAGE, so the page count can no longer be derived from its
  // length — it comes from `paging.total`, the count of rows matching the filters. Deriving it
  // locally would have shown "1 page" forever once the server started sending ten rows.
  const paging = useSelector(selectOpportunityPaging);
  const [activePage, setActivePage] = useState(1);
  const itemsPerPage = paging.limit || 10;
  const totalPages = Math.max(1, Math.ceil((paging.total || 0) / itemsPerPage));

  // View mode, persisted per browser. Spreadsheet is the default: it is the only view that shows
  // the whole page at a glance with tier, role size and series together.
  //
  // The stored value is MIGRATED, not trusted. 'compact' and 'standard' were removed, and a
  // browser that last used one still has it in localStorage — left alone it would select a mode
  // that no longer exists, and the dropdown would render blank with no way back except clearing
  // site data.
  const [viewMode, setViewMode] = useState(() => {
    const stored = localStorage.getItem('opportunityViewMode');
    return (stored === 'detailed' || stored === 'spreadsheet') ? stored : 'spreadsheet';
  });

  // Same helper the slice seeds from — validated, and safe when localStorage throws.
  const [sortKey, setSortKey] = useState(readStoredSort);

  const sortOptions = [
    { key: 'tier', value: 'tier', text: 'Role size (leads first)', icon: 'sort amount down' },
    // The composite the old grid produced implicitly, before flattening the rows for pagination
    // removed it: series alphabetically, characters alphabetically inside each series.
    { key: 'series', value: 'series', text: 'Series, then character', icon: 'folder open' },
    { key: 'alpha', value: 'alpha', text: 'Character name (A-Z)', icon: 'sort alphabet down' },
    { key: 'lines', value: 'lines', text: 'Most dialogue lines', icon: 'comments' },
    { key: 'recent', value: 'recent', text: 'Most recently posted', icon: 'clock outline' }
  ];

  // View mode options for dropdown
  const viewModeOptions = [
    { key: 'spreadsheet', value: 'spreadsheet', text: 'Spreadsheet View', icon: 'table' },
    { key: 'detailed', value: 'detailed', text: 'Detailed View', icon: 'content' }
  ];

  // No local slicing. The server cut this page before hydrating it, which is the whole point of
  // the change: it used to send every matching row (capped at 1,000, which silently hid 512 of
  // 1,512 open opportunities and five entire series) so the browser could throw most away.
  const currentOpportunities = opportunities;

  // Fetch opportunities and applications on component mount
  useEffect(() => {
    // Sending `limit` is what opts this caller into the paginated envelope; without it the
    // server answers with a bare array, as it still does for the Book-Creator pages.
    dispatch(fetchOpportunities({
      status: 'Open',
      limit: itemsPerPage,
      offset: 0,
      sort: sortKey
    }));

    if (actorId) {
      dispatch(fetchActorApplications(actorId));
    }
    // Mount-only on purpose. itemsPerPage and sortKey are read for their INITIAL values (page
    // size, and the sort restored from localStorage); listing them would re-fire this fetch every
    // time the server echoed a limit back into paging, which is a loop, not a refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, actorId]); // Remove status dependency to ensure it always fetches

  // Simple callback that ONLY resets pagination when a new search is initiated
  // The actual search is handled internally by OpportunityFilterEnhanced
  const handleSearchInitiated = React.useCallback(() => {
    setActivePage(1); // Reset to first page when new search starts
  }, []);

  // Keep old handler for reference but it's no longer used
  // const handleFilterChange = React.useCallback((filters) => {
  //   dispatch(setFilters(filters));
  //   dispatch(fetchOpportunities(filters));
  //   setActivePage(1); // Reset to first page when filters change
  // }, [dispatch]);

  // Turning a page is a SERVER round trip now, and it must re-issue the search that produced the
  // current result set — not a bare fetch. `paging.lastFilters` carries the filter panel's
  // selections (the panel owns the filters, this page owns the pager), so page 2 of a filtered
  // search stays inside that filter instead of quietly becoming page 2 of everything.
  const handlePageChange = (e, { activePage: nextPage }) => {
    setActivePage(nextPage);
    dispatch(fetchOpportunities({
      ...(paging.lastFilters || { status: 'Open' }),
      limit: itemsPerPage,
      offset: (nextPage - 1) * itemsPerPage,
      sort: sortKey
    }));
  };

  // Sort is a server concern once the list is paged — sorting a 10-row page in the browser would
  // only reorder the page, not the corpus. Persisted per browser so a preferred order survives
  // the next visit; the shipped default is tier-descending so the 108 leads are not scattered
  // through 879 walk-ons.
  const handleSortChange = (e, { value }) => {
    setSortKey(value);
    try { localStorage.setItem('opportunitySortKey', value); } catch (_) { /* preference only */ }
    setActivePage(1);
    dispatch(setSort(value));
    dispatch(fetchOpportunities({
      ...(paging.lastFilters || { status: 'Open' }),
      limit: itemsPerPage,
      offset: 0,
      sort: value
    }));
  };

  // Handle view details button click
  const handleViewDetails = (opportunityId) => {
    navigate(`/opportunities/${opportunityId}`, {
      state: {
        from: '/opportunities',
        backButtonText: 'Back to Marketplace'
      }
    });
  };

  // Handle apply button click
  const handleApply = (opportunityId) => {
    navigate(`/opportunities/${opportunityId}/apply`, {
      state: {
        from: '/opportunities',
        backButtonText: 'Back to Marketplace'
      }
    });
  };

  // Check if the current actor has applied for an opportunity
  const hasApplied = (opportunityId) => {
    return actorApplications.some(app => app.opportunity?.uuid === opportunityId);
  };

  // Add a manual refresh function
  const handleRefresh = () => {
    dispatch(fetchOpportunities({ status: 'Open' }));
    if (actorId) {
      dispatch(fetchActorApplications(actorId));
    }
  };

  // Handle view mode change
  const handleViewModeChange = (e, { value }) => {
    setViewMode(value);
    localStorage.setItem('opportunityViewMode', value);
  };


  // Render loading state
  //
  // 2026-05-14 — Disabled per operator request. This early-return full-page
  // loader was too disruptive: clicking "Search Now" from a zero-results
  // state replaced the entire marketplace (including the filter form) with
  // a blank "Loading Opportunities" segment, costing the user their filter
  // context for the duration of the fetch.
  //
  // Pair-suppressed with the Search Now button's inline spinner and the
  // Search input's leading-icon spinner in OpportunityFilterEnhanced.jsx.
  // The button's `disabled={ui.isSearching || filtersAreAtDefault}` is
  // enough to prevent double-fires; the empty-state and results renderings
  // below already handle every other case (success, error, no-results).
  //
  // Kept commented (not deleted) so we can re-enable cleanly if we ever
  // decide we want a global loader back — e.g. for a cold-cache, first-time
  // marketplace open where opportunities[] is genuinely empty *and* the
  // request is in flight.
  // if (status === 'loading' && opportunities.length === 0) {
  //   return (
  //     <HeaderAndPage
  //       doCenter={true}
  //       headerImage={microphoneStand}
  //       headerText="Voice Opportunities Marketplace"
  //       subHeaderText="Find voice acting opportunities that match your skills"
  //     >
  //       <Segment basic textAlign="center" style={{ minHeight: '300px' }}>
  //         <Loader active size="large">Loading Opportunities</Loader>
  //         <div style={{ marginTop: '20px' }}>
  //           <Button onClick={handleRefresh}>
  //             <Icon name="refresh" /> Refresh
  //           </Button>
  //         </div>
  //       </Segment>
  //     </HeaderAndPage>
  //   );
  // }

  // Render error state
  if (status === 'failed') {
    return (
      <HeaderAndPage
        doCenter={true}
        headerImage={microphoneStand}
        headerText="Voice Opportunities Marketplace"
        subHeaderText="Find voice acting opportunities that match your skills"
      >
        <OpportunityFilterEnhanced onSearchInitiated={handleSearchInitiated} />
        <Segment placeholder>
          <Header icon>
            <Icon name="warning sign" />
            <div>Unable to Load Opportunities</div>
          </Header>
          <p>We encountered an issue while trying to load opportunities.</p>

          {/* Display a user-friendly error message */}
          <Message negative compact style={{ maxWidth: '80%', margin: '0 auto' }}>
            <Message.Header>Technical Details</Message.Header>
            <p>
              {error && (
                typeof error === 'string'
                  ? (error.includes('<!DOCTYPE')
                    ? 'The server returned an HTML page instead of data. This usually indicates a server configuration issue.'
                    : error)
                  : (error?.message || error?.error || JSON.stringify(error))
              )}
            </p>

          </Message>

          <div style={{ marginTop: '20px' }}>
            <Button primary onClick={() => dispatch(fetchOpportunities())}>
              <Icon name="refresh" /> Try Again
            </Button>
          </div>
        </Segment>
      </HeaderAndPage>
    );
  }

  // Render empty state
  if (opportunities.length === 0) {
    return (
      <HeaderAndPage
        doCenter={true}
        headerImage={microphoneStand}
        headerText="Voice Opportunities Marketplace"
        subHeaderText="Find voice acting opportunities that match your skills"
      >
        <OpportunityFilterEnhanced onSearchInitiated={handleSearchInitiated} />
        <Segment placeholder textAlign="center" style={{ marginBottom: "70px" }}>
          <Header icon>
            <Image src={microphoneStand} size="tiny" centered />
            <div style={{ marginTop: '15px' }}>No Opportunities Found</div>
          </Header>
          <p>There are no voice acting opportunities matching your filters.</p>
          <p>Try adjusting your search criteria or check back later for new opportunities.</p>
        </Segment>
        {AuthStdFooter()}
      </HeaderAndPage>
    );
  }

  // Render opportunities
  return (
    <HeaderAndPage
      doCenter={true}
      headerImage={microphoneStand}
      headerText="Voice Opportunities Marketplace"
      subHeaderText="Find voice acting opportunities that match your skills"

    >
      <OpportunityFilterEnhanced onSearchInitiated={handleSearchInitiated} />
      <Segment style={{ marginBottom: "70px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={8}>
              <Header as="h3">Available Opportunities</Header>
              {/* Both halves of this used to read from the same array, so it said "10 of 10"
                * while the server was quietly withholding 512 rows. The range is now the page
                * within the true filtered total. */}
              <p>
                {paging.total > 0
                  ? `Showing ${(activePage - 1) * itemsPerPage + 1}–${Math.min(activePage * itemsPerPage, paging.total)} of ${paging.total} opportunities`
                  : 'No opportunities match these filters'}
              </p>
            </Grid.Column>
            <Grid.Column width={8} textAlign="right">
              <Dropdown
                selection
                value={sortKey}
                options={sortOptions}
                onChange={handleSortChange}
                style={{ minWidth: '210px', marginRight: '8px' }}
              />
              <Dropdown
                selection
                value={viewMode}
                options={viewModeOptions}
                onChange={handleViewModeChange}
                // 170px clipped "Spreadsheet View" onto a second line under its icon. 205 keeps
                // the longest option on one line with room for the dropdown caret.
                style={{ minWidth: '205px' }}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        {viewMode === 'spreadsheet' ? (
          <OpportunitySpreadsheet
            opportunities={currentOpportunities}
            onViewDetails={handleViewDetails}
            onApply={handleApply}
            hasApplied={hasApplied}
          />
        ) : (
          <Grid stackable>
            {currentOpportunities.map(opportunity => (
              <Grid.Column width={8} key={opportunity.uuid} style={{ marginBottom: '0px' }}>
                <OpportunityCard
                  opportunity={opportunity}
                  onViewDetails={handleViewDetails}
                  onApply={handleApply}
                  hasApplied={hasApplied(opportunity.uuid)}
                  isVoiceActor={isVoiceActor}
                />
              </Grid.Column>
            ))}
          </Grid>
        )}

        {totalPages > 1 && (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Pagination
              activePage={activePage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              ellipsisItem={{ content: '...', icon: 'ellipsis horizontal' }}
              firstItem={{ content: '«', icon: 'angle double left' }}
              lastItem={{ content: '»', icon: 'angle double right' }}
              prevItem={{ content: '‹', icon: 'angle left' }}
              nextItem={{ content: '›', icon: 'angle right' }}
            />
          </div>
        )}
      </Segment>
      {AuthStdFooter()}
    </HeaderAndPage>

  );
};

export default OpportunityMarketplace;
